/* eslint-disable @typescript-eslint/no-use-before-define */
import type { LiteralUnion } from 'type-fest';
import { getColorFormulaFromCSSText } from '@/brandSDK';
import type { IBrandLinearGradientColorFormula } from '@/brandSDK/types';
import request from '@/utils/request';

let brandEntry: IYunPanEntry | null = null;

export const getBrandListAndMode = async (opts?: {
    orderBy?: LiteralUnion<'id,asc' | 'id,desc', string>;
}): Promise<{
    brandEntryMode: string;
    brandList: IBrand[];
} | null> => {
    if (!brandEntry) {
        const entries = await getYupanEntry();
        brandEntry = entries.find((e) => e.mode === 'brand') ?? null;
    }
    if (!brandEntry) return null;
    const BrandPageCount = 100;
    const res = await getNodeList({
        currentNode: brandEntry.enter_node,
        mode: brandEntry.mode,
        count: BrandPageCount,
        order_by: opts?.orderBy ?? 'id,asc',
    });
    const nodeList = res.data;
    const brandList: IBrand[] = nodeList.map((node) => {
        return {
            id: node.id,
            name: node.name,
            currentNode: node.current_node,
        };
    });
    return {
        brandEntryMode: brandEntry.mode,
        brandList,
    };
};

const MAX_COLOR_COUNT = 50;
const COLOR_ATTR2 = '6';

export const fetchColorCards = (brandCurrentNode: string, mode: string): Promise<ColorCard[]> =>
    request<any, { data: { id: number; name: string; biz_ext: string }[] }>('/yunpan/node/list', {
        method: 'GET',
        params: {
            current_node: brandCurrentNode,
            mode,
            attr1: 2,
            attr2: COLOR_ATTR2,
            count: MAX_COLOR_COUNT,
            cursor: '',
            order_by: 'id,asc',
        },
    }).then(({ data: nodeList }) => {
        return nodeList.map<ColorCard>((node) => {
            const bizExt = JSON.parse(node.biz_ext);
            let ext: (string | IBrandLinearGradientColorFormula)[];
            try {
                ext = bizExt.ext === '' ? [] : JSON.parse(bizExt.ext);
                // 国内渐变色也为 string, 这里转换为 IBrandLinearGradientColorFormula
                ext = ext.map((color) => (typeof color === 'string' ? getColorFormulaFromCSSText(color) : color));
                if (!Array.isArray(ext)) {
                    throw new Error('colors is not array');
                }
            } catch (error) {
                console.log('===biz_ext解析失败', error, bizExt);
                ext = [];
            }
            return {
                id: node.id,
                name: node.name,
                colors: ext,
            };
        });
    });

export interface ColorCard {
    id: number;
    name: string;
    colors: (string | IBrandLinearGradientColorFormula)[];
}

interface IYunPanEntry {
    mode: 'private' | 'shareme' | 'brand' | 'template_material';
    enter_node: string;
}

export interface IBrand {
    id: number;
    name: string;
    currentNode: string;
}

interface NodeInfo {
    /** 节点id */
    id: number;
    created_at: number;
    updated_at: number;
    /** 节点类型 1目录 2文件 */
    type: 1 | 2;
    /** 节点名 */
    name: string;
    /** 节点大小 */
    size: number;
    /** 源文件, 模板的src为 "nonhttp://refer?type=template&id=234" */
    src: string;
    biz_ext: string;
    ext: {
        // 目录则Count有值
        count?: { dir_count: number; file_count: number };
        // 文件则preview有值
        preview?: {
            url: string;
            width: number;
            height: number;
        } | null;
        // 目录则previews有值
        previews?:
            | {
                  url: string;
                  width: number;
                  height: number;
              }[]
            | null;
        extra?: {
            /** 当前node为品牌规范时, 该值生效, 为品牌规范下首个logo预览图 */
            logo_preview?: {
                url: string;
                width: number;
                height: number;
            };
            /** 当前node为品牌规范时, 该值生效, 为品牌规范下首个色卡信息的json字符串 */
            color_ext?: string;
        };
    };
    /** 是否选中 */
    selected?: boolean;
    current_node: string;
    attr2: number;
}

/**
 * https://api-mock.meitu-int.com/mock/2110/yunpan/entry
 * [空间]云盘各个入口文件夹地址
 * 这个接口可以替代  /yunpan/space/info 接口
 */
const getYupanEntry = () =>
    request<any, IYunPanEntry[]>(`/yunpan/entry`, {
        method: 'GET',
    });

const getNodeList = async function ({
    currentNode = '',
    type = 1,
    count = 20,
    attr1,
    attr2,
    cursor = '',
    mode = 'private',
    order_by,
}: {
    currentNode: string;
    type?: number;
    count?: number;
    attr1?: number;
    attr2?: number;
    cursor?: string;
    mode?: LiteralUnion<'enterprise' | 'private' | 'shareme', string>;
    /**
     * 排序规则: 如 'id,asc'
     * 字段逗号方向，  支持的字段更新时间updated_at   创建时间 id  名称 name， 方向 asc 升序， desc 降序
     */
    order_by?: string;
}) {
    return await request<any, { data: NodeInfo[]; cursor: '' }>(`/yunpan/node/list`, {
        method: 'GET',
        params: {
            count,
            current_node: currentNode,
            type,
            attr1,
            attr2,
            cursor,
            mode,
            order_by,
        },
    }).then((res) => {
        res.data.forEach((item) => {
            if (item.ext.extra) {
                try {
                    item.ext.extra = JSON.parse(item.ext.extra as string);
                } catch {
                    /* noop */
                }
            }
        });
        return res;
    });
};
